import type {
  Camera,
  CameraGroupRef,
  CameraStatus,
  CameraType,
  PaginatedResult,
  RecordingMode,
} from './types.ts';

/** Raw camera row from Adonis `CameraTransformer` or Flashback NVR DTO. */
export type AdonisCameraRaw = Record<string, unknown>;

function pickString(raw: AdonisCameraRaw, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return '';
}

function pickBool(raw: AdonisCameraRaw, ...keys: string[]): boolean {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'boolean') return v;
    if (v === 1 || v === '1' || v === 'true') return true;
    if (v === 0 || v === '0' || v === 'false') return false;
  }
  return false;
}

function pickNullableNumber(raw: AdonisCameraRaw, ...keys: string[]): number | null {
  for (const key of keys) {
    const v = raw[key];
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function deriveCameraType(raw: AdonisCameraRaw): CameraType {
  const explicit = pickString(raw, 'type');
  if (explicit === 'standard' || explicit === 'ptz' || explicit === '360') {
    return explicit;
  }
  const has360 = Boolean(pickString(raw, 'cam360Rtsp', 'cam360_rtsp'));
  const hasPtz = Boolean(pickString(raw, 'ptzRtsp', 'ptz_rtsp'));
  if (has360 && hasPtz) return '360';
  if (hasPtz) return 'ptz';
  if (has360) return '360';
  return 'standard';
}

function deriveRtspUrl(raw: AdonisCameraRaw): string {
  const direct = pickString(raw, 'rtspUrl', 'rtsp_url');
  if (direct) return direct;
  const cam360 = pickString(raw, 'cam360Rtsp', 'cam360_rtsp');
  if (cam360) return cam360;
  return pickString(raw, 'ptzRtsp', 'ptz_rtsp', 'streaming_url', 'stream_url');
}

function deriveStatus(raw: AdonisCameraRaw): CameraStatus {
  const status = pickString(raw, 'status');
  if (status === 'active' || status === 'inactive') return status;
  return pickBool(raw, 'is_active', 'isActive') ? 'active' : 'inactive';
}

function deriveRecordingMode(raw: AdonisCameraRaw): RecordingMode {
  const mode = pickString(raw, 'recordingMode', 'recording_mode', 'recording_type', 'recordingType');
  if (mode === 'continuous' || mode === 'scheduled' || mode === 'manual') return mode;
  return 'continuous';
}

function deriveGroup(raw: AdonisCameraRaw): CameraGroupRef | null {
  const embedded = raw.group;
  if (embedded && typeof embedded === 'object' && !Array.isArray(embedded)) {
    const g = embedded as Record<string, unknown>;
    const id = pickNullableNumber(g, 'id');
    const name = pickString(g, 'name');
    if (id != null && name) return { id, name };
  }
  const groupId = pickNullableNumber(raw, 'groupId', 'group_id');
  if (groupId == null) return null;
  const name = pickString(raw, 'group_name', 'groupName');
  return name ? { id: groupId, name } : null;
}

/** Map one Adonis/NVR camera payload to Flashback `Camera`. */
export function cameraFromAdonis(raw: AdonisCameraRaw): Camera {
  const id = pickString(raw, 'id');
  if (!id) {
    throw new Error('Camera payload missing id');
  }

  const manualStarted =
    raw.manualRecordingStartedAt ??
    raw.manual_recording_started_at ??
    null;

  const has360 = Boolean(pickString(raw, 'cam360Rtsp', 'cam360_rtsp'));
  const hasPtz = Boolean(pickString(raw, 'ptzRtsp', 'ptz_rtsp'));

  return {
    id,
    name: pickString(raw, 'name') || 'Camera',
    groupId: pickNullableNumber(raw, 'groupId', 'group_id'),
    group: deriveGroup(raw),
    rtspUrl: deriveRtspUrl(raw),
    type: deriveCameraType(raw),
    dualStream:
      pickBool(raw, 'dualStream', 'dual_stream') || (has360 && hasPtz),
    recordingMode: deriveRecordingMode(raw),
    recordingEnabled: pickBool(raw, 'recordingEnabled', 'recording_enabled') || true,
    status: deriveStatus(raw),
    isFavorite: pickBool(raw, 'isFavorite', 'is_favorite'),
    manualRecordingStartedAt:
      manualStarted === null || manualStarted === undefined
        ? null
        : String(manualStarted),
    createdAt: String(raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? new Date().toISOString()),
  };
}

/** Wrap a plain array as paginated Flashback list (until backend returns native pagination). */
export function paginateCameras(
  items: Camera[],
  query: { page?: number; pageSize?: number },
): PaginatedResult<Camera> {
  const page = Math.max(1, query.page ?? 1);
  const pageSize = Math.max(1, Math.min(100, query.pageSize ?? 48));
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total: items.length,
    page,
    pageSize,
  };
}

/** Unwrap `{ success, data }` or a bare array from list endpoints. */
export function unwrapCameraListPayload(data: unknown): AdonisCameraRaw[] {
  if (Array.isArray(data)) {
    return data.filter((r): r is AdonisCameraRaw => r != null && typeof r === 'object');
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const inner = o.items ?? o.data;
    if (Array.isArray(inner)) {
      return inner.filter((r): r is AdonisCameraRaw => r != null && typeof r === 'object');
    }
    if (inner && typeof inner === 'object') {
      const paginated = inner as Record<string, unknown>;
      if (Array.isArray(paginated.items)) {
        return paginated.items.filter(
          (r): r is AdonisCameraRaw => r != null && typeof r === 'object',
        );
      }
    }
  }
  return [];
}
